/**
 * CR-1154: Hyperlink to the PO line item detail ("Sales Order Line Item" in
 * supplier mode — the same screen, portal-relative label).
 *
 * The PO line detail is a modal on the Purchasing page, deep-linkable as
 * `/purchasing?tab=po_lines&line=<POLinePK>`. It keys off the line's **primary
 * key**, never the human `line_number`.
 *
 * Degrades to plain text when no line resolves (e.g. an NCR whose import never
 * matched a PO line, or a forecast row that has no line at all), so callers can
 * render this unconditionally.
 *
 * @param poLineId - The PurchaseOrderLine primary key. Null/undefined renders children as plain text.
 * @param children - The label to render (e.g. the line number, or "PO / Line").
 * @param className - Optional override for the link styling.
 * @returns A router link to the PO line item detail, or the bare children.
 *
 * @example
 * <PoLineLink poLineId={grn.po_line}>{grn.line_number}</PoLineLink>
 */
import { Link } from 'react-router-dom';
import { ReactNode } from 'react';

interface PoLineLinkProps {
  poLineId?: number | string | null;
  children: ReactNode;
  className?: string;
}

/** Canonical deep-link for a PO line item detail. */
export function poLineHref(poLineId: number | string): string {
  return `/purchasing?tab=po_lines&line=${poLineId}`;
}

export default function PoLineLink({
  poLineId,
  children,
  className = 'text-primary hover:underline',
}: PoLineLinkProps) {
  if (poLineId === null || poLineId === undefined || poLineId === '') {
    return <>{children}</>;
  }
  // CR-1154: open in a new tab (modals/tables). The generic drill-down renderer
  // deliberately stays same-tab — see createFieldLink / CR-5363.
  return (
    <Link to={poLineHref(poLineId)} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </Link>
  );
}
